import { useMemo } from "react";

import { useFertilizerHelpers, useTillageAndSeedingHelpers, useTranslation } from "utils";

import { CROP_TYPE_FCPO, FieldPracticeResponse, PRACTICE_SLUG } from "models";

import { FieldCropData, FieldFertilizerData, FieldSeedingData, FieldTillageData } from "pages/admin/verifications/utils/fieldPracticeFilterUtils";

type FieldPracticeData = {
  fieldId: string;
  data: FieldPracticeResponse | undefined;
  updatedAt: number;
};

type UseFieldPracticesFilterDataParams = {
  fieldPracticesData: FieldPracticeData[];
  isCropsPractice: boolean;
  isFertilizerPractice: boolean;
  isTillageSeedingPractice: boolean;
};

type UseFieldPracticesFilterDataReturn = {
  fieldCropsMap: Map<string, FieldCropData[]> | null;
  fieldFertilizersMap: Map<string, FieldFertilizerData[]> | null;
  fieldTillagesMap: Map<string, FieldTillageData[]> | null;
  fieldSeedingsMap: Map<string, FieldSeedingData[]> | null;
  uniqueCrops: string[];
  availableCropTypes: CROP_TYPE_FCPO[];
  availableYears: number[];
  uniqueFertilizers: string[];
  availableFertilizerTypes: ("mineralFertilizers" | "organicFertilizers")[];
  availableFertilizerYears: number[];
  uniqueTillages: string[];
  uniqueSeedings: string[];
  availableTillageYears: number[];
  availableSeedingYears: number[];
};

export const useFieldPracticesFilterData = ({
  fieldPracticesData,
  isCropsPractice,
  isFertilizerPractice,
  isTillageSeedingPractice,
}: UseFieldPracticesFilterDataParams): UseFieldPracticesFilterDataReturn => {
  const { t } = useTranslation(["practices"], { useSuspense: false });
  const { getFertilizerName } = useFertilizerHelpers();
  const { getPracticeName, getCombinedPractices } = useTillageAndSeedingHelpers();

  // Create a map of fieldId -> crops for efficient filtering
  const fieldCropsMap = useMemo(() => {
    if (!isCropsPractice) return null;

    const map = new Map<string, FieldCropData[]>();

    fieldPracticesData.forEach(({ fieldId, data }) => {
      if (data?.crops && data.crops.length > 0 && fieldId) {
        map.set(
          fieldId,
          data.crops.map((crop) => {
            // Translate crop name if translationSlug exists, otherwise use cropName
            // Fallback to "Unknown Crop" if neither is available
            const cropName = crop.cropName || (crop.translationSlug ? t(`crops.${crop.translationSlug}`) : "Unknown Crop");

            return {
              cropName,
              year: crop.year,
              cropType: crop.cropType,
            };
          })
        );
      }
    });

    return map;
  }, [isCropsPractice, fieldPracticesData, t]);

  // Extract unique crops from all fields
  const uniqueCrops = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const cropSet = new Set<string>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.cropName && crop.cropName.trim() !== "") {
          cropSet.add(crop.cropName);
        }
      });
    });

    return Array.from(cropSet).sort();
  }, [fieldCropsMap]);

  // Extract available crop types from all fields
  const availableCropTypes = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const cropTypeSet = new Set<CROP_TYPE_FCPO>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.cropType) {
          cropTypeSet.add(crop.cropType);
        }
      });
    });

    return Array.from(cropTypeSet);
  }, [fieldCropsMap]);

  // Extract available years from all fields
  const availableYears = useMemo(() => {
    if (!fieldCropsMap || fieldCropsMap.size === 0) return [];

    const yearSet = new Set<number>();
    fieldCropsMap.forEach((crops) => {
      crops.forEach((crop) => {
        if (crop.year) {
          yearSet.add(crop.year);
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [fieldCropsMap]);

  // Create a map of fieldId -> fertilizers for efficient filtering
  const fieldFertilizersMap = useMemo(() => {
    if (!isFertilizerPractice) return null;

    const map = new Map<string, FieldFertilizerData[]>();

    fieldPracticesData.forEach(({ fieldId, data }) => {
      if (data?.practices && data.practices.length > 0 && fieldId) {
        const fertilizers = data.practices
          .filter((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS)
          .map((practice) => {
            const fertilizerName = getFertilizerName(practice.type, practice.assignedPracticeId, "Unknown Fertilizer");

            return {
              fertilizerName,
              year: practice.year,
              type: practice.type as "mineralFertilizers" | "organicFertilizers",
              assignedPracticeId: practice.assignedPracticeId,
            };
          });

        if (fertilizers.length > 0) {
          map.set(fieldId, fertilizers);
        }
      }
    });

    return map;
  }, [isFertilizerPractice, fieldPracticesData, getFertilizerName]);

  // Extract unique fertilizers from all fields
  const uniqueFertilizers = useMemo(() => {
    if (!fieldFertilizersMap || fieldFertilizersMap.size === 0) return [];

    const fertilizerSet = new Set<string>();
    fieldFertilizersMap.forEach((fertilizers) => {
      fertilizers.forEach((fertilizer) => {
        if (fertilizer.fertilizerName && fertilizer.fertilizerName.trim() !== "") {
          fertilizerSet.add(fertilizer.fertilizerName);
        }
      });
    });

    return Array.from(fertilizerSet).sort();
  }, [fieldFertilizersMap]);

  // Extract available fertilizer types from all fields
  const availableFertilizerTypes = useMemo(() => {
    if (!fieldFertilizersMap || fieldFertilizersMap.size === 0) return [];

    const fertilizerTypeSet = new Set<"mineralFertilizers" | "organicFertilizers">();
    fieldFertilizersMap.forEach((fertilizers) => {
      fertilizers.forEach((fertilizer) => {
        if (fertilizer.type) {
          fertilizerTypeSet.add(fertilizer.type);
        }
      });
    });

    return Array.from(fertilizerTypeSet);
  }, [fieldFertilizersMap]);

  // Extract available fertilizer years from all fields
  const availableFertilizerYears = useMemo(() => {
    if (!fieldFertilizersMap || fieldFertilizersMap.size === 0) return [];

    const yearSet = new Set<number>();
    fieldFertilizersMap.forEach((fertilizers) => {
      fertilizers.forEach((fertilizer) => {
        if (fertilizer.year) {
          yearSet.add(fertilizer.year);
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [fieldFertilizersMap]);

  // Create a map of fieldId -> tillages for efficient filtering
  const fieldTillagesMap = useMemo(() => {
    if (!isTillageSeedingPractice) return null;

    const map = new Map<string, FieldTillageData[]>();

    fieldPracticesData.forEach(({ fieldId, data }) => {
      if (data?.practices && data.practices.length > 0 && fieldId) {
        const tillages = data.practices
          .filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES)
          .map((practice) => {
            const practiceName = getPracticeName(practice.type, practice.assignedPracticeId, "Unknown Tillage");

            return {
              practiceName,
              year: practice.year,
              type: "tillages" as const,
              assignedPracticeId: practice.assignedPracticeId,
            };
          });

        if (tillages.length > 0) {
          map.set(fieldId, tillages);
        }
      }
    });

    return map;
  }, [isTillageSeedingPractice, fieldPracticesData, getPracticeName]);

  // Create a map of fieldId -> seedings for efficient filtering
  const fieldSeedingsMap = useMemo(() => {
    if (!isTillageSeedingPractice) return null;

    const map = new Map<string, FieldSeedingData[]>();

    fieldPracticesData.forEach(({ fieldId, data }) => {
      if (data && fieldId) {
        // Use getCombinedPractices to get seedings from crops
        const combinedPractices = getCombinedPractices(data);
        const seedings = combinedPractices
          .filter((practice) => (practice.type as string) === PRACTICE_SLUG.SEEDINGS)
          .map((practice) => {
            const practiceName = getPracticeName(practice.type, practice.assignedPracticeId, "Unknown Seeding");

            return {
              practiceName,
              year: practice.year,
              type: "seedings" as const,
              assignedPracticeId: practice.assignedPracticeId,
            };
          });

        if (seedings.length > 0) {
          map.set(fieldId, seedings);
        }
      }
    });

    return map;
  }, [isTillageSeedingPractice, fieldPracticesData, getPracticeName, getCombinedPractices]);

  // Extract unique tillages from all fields
  const uniqueTillages = useMemo(() => {
    if (!fieldTillagesMap || fieldTillagesMap.size === 0) return [];

    const tillageSet = new Set<string>();
    fieldTillagesMap.forEach((tillages) => {
      tillages.forEach((tillage) => {
        if (tillage.practiceName && tillage.practiceName.trim() !== "") {
          tillageSet.add(tillage.practiceName);
        }
      });
    });

    return Array.from(tillageSet).sort();
  }, [fieldTillagesMap]);

  // Extract unique seedings from all fields
  const uniqueSeedings = useMemo(() => {
    if (!fieldSeedingsMap || fieldSeedingsMap.size === 0) return [];

    const seedingSet = new Set<string>();
    fieldSeedingsMap.forEach((seedings) => {
      seedings.forEach((seeding) => {
        if (seeding.practiceName && seeding.practiceName.trim() !== "") {
          seedingSet.add(seeding.practiceName);
        }
      });
    });

    return Array.from(seedingSet).sort();
  }, [fieldSeedingsMap]);

  // Extract available tillage years from all fields
  const availableTillageYears = useMemo(() => {
    if (!fieldTillagesMap || fieldTillagesMap.size === 0) return [];

    const yearSet = new Set<number>();
    fieldTillagesMap.forEach((tillages) => {
      tillages.forEach((tillage) => {
        if (tillage.year) {
          yearSet.add(tillage.year);
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [fieldTillagesMap]);

  // Extract available seeding years from all fields
  const availableSeedingYears = useMemo(() => {
    if (!fieldSeedingsMap || fieldSeedingsMap.size === 0) return [];

    const yearSet = new Set<number>();
    fieldSeedingsMap.forEach((seedings) => {
      seedings.forEach((seeding) => {
        if (seeding.year) {
          yearSet.add(seeding.year);
        }
      });
    });

    return Array.from(yearSet).sort((a, b) => a - b);
  }, [fieldSeedingsMap]);

  return {
    fieldCropsMap,
    fieldFertilizersMap,
    fieldTillagesMap,
    fieldSeedingsMap,
    uniqueCrops,
    availableCropTypes,
    availableYears,
    uniqueFertilizers,
    availableFertilizerTypes,
    availableFertilizerYears,
    uniqueTillages,
    uniqueSeedings,
    availableTillageYears,
    availableSeedingYears,
  };
};
